/**
 * Pure rules the views share: cycle status, the live ballot, date wording and
 * Directory filtering. No DOM, so it is unit-tested directly.
 *
 * Dates are ISO `YYYY-MM-DD` strings from Backbone, compared as calendar days.
 */
import type { PublicCycleSummary } from "./api";
import type { CycleStatus, DirectoryFilters } from "./router";

type Ballot = { slug: string; label: string; date: string; stage: string };

const DAY_MS = 86_400_000;
/** A ballot this close either side of today makes its cycle current. */
const CURRENT_WINDOW_DAYS = 60;

/** Today in the visitor's own calendar, not UTC — election day is local. */
export function localToday(now = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function dayNumber(iso: string): number {
  return Math.round(Date.parse(`${iso}T00:00:00Z`) / DAY_MS);
}

/**
 * `current` while any ballot is within 60 days of today; `upcoming` while a
 * ballot is still ahead (or none is confirmed yet); otherwise `past`. "Still
 * ahead" rather than "first ballot ahead", so a cycle between its primary
 * and its general is never filed as past.
 */
export function cycleStatus(cycle: PublicCycleSummary, today: string): CycleStatus {
  const t = dayNumber(today);
  if (cycle.ballots.some((b) => Math.abs(dayNumber(b.date) - t) <= CURRENT_WINDOW_DAYS)) return "current";
  if (cycle.ballots.length === 0 || cycle.ballots.some((b) => b.date >= today)) return "upcoming";
  return "past";
}

/** The ballot the Election view opens on: the next one, else the most recent (ADR 0005). */
export function liveBallot<B extends Ballot>(ballots: B[], today: string): B | null {
  const sorted = [...ballots].sort((a, b) => a.date.localeCompare(b.date));
  return sorted.find((b) => b.date >= today) ?? sorted[sorted.length - 1] ?? null;
}

const MONTH_DAY = new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", timeZone: "UTC" });
const MONTH_DAY_YEAR = new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
const SHORT_MONTH = new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" });

const asDate = (iso: string) => new Date(`${iso}T12:00:00Z`);

export function formatDate(iso: string, withYear = true): string {
  return (withYear ? MONTH_DAY_YEAR : MONTH_DAY).format(asDate(iso));
}

/** `{ month: "Feb", day: "23", year: "2027" }` for stacked date columns. */
export function dateParts(iso: string): { month: string; day: string; year: string } {
  return { month: SHORT_MONTH.format(asDate(iso)), day: String(Number(iso.slice(8, 10))), year: iso.slice(0, 4) };
}

/**
 * "February 23, runoff April 7": each ballot by date, named by its stage
 * unless it is the cycle's leading general. A ballot in a later year than the
 * first carries its year.
 */
export function formatBallotDates(ballots: Ballot[]): string {
  const sorted = [...ballots].sort((a, b) => a.date.localeCompare(b.date));
  const firstYear = sorted[0]?.date.slice(0, 4);
  const text = sorted
    .map((b, i) => {
      const date = formatDate(b.date, b.date.slice(0, 4) !== firstYear);
      return i === 0 && b.stage === "general" ? date : `${b.stage} ${date}`;
    })
    .join(", ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "city" → "City"; Backbone reports jurisdiction as a lower-case level. */
export function jurisdictionLabel(value: string): string {
  return value.replace(/\b\p{L}/gu, (c) => c.toUpperCase());
}

export function cycleYears(cycles: PublicCycleSummary[]): string[] {
  const years = new Set(cycles.flatMap((c) => c.ballots.map((b) => b.date.slice(0, 4))));
  return [...years].sort((a, b) => b.localeCompare(a));
}

/** Remove URL filters that cannot be represented by the loaded Directory. */
export function normalizeDirectoryFilters(
  cycles: PublicCycleSummary[],
  filters: DirectoryFilters
): { filters: DirectoryFilters; changed: boolean } {
  const years = cycleYears(cycles);
  const jurisdictions = new Set(cycles.flatMap((cycle) => cycle.jurisdictions));
  const normalized: DirectoryFilters = {
    ...filters,
    year: filters.year && years.includes(filters.year) ? filters.year : null,
    jurisdiction:
      filters.jurisdiction && jurisdictions.has(filters.jurisdiction)
        ? filters.jurisdiction
        : null,
  };
  return {
    filters: normalized,
    changed:
      normalized.year !== filters.year ||
      normalized.jurisdiction !== filters.jurisdiction,
  };
}

export function filterCycles(
  cycles: PublicCycleSummary[],
  filters: DirectoryFilters,
  today: string
): PublicCycleSummary[] {
  const query = filters.q.trim().toLocaleLowerCase("en-US");
  return cycles.filter((c) => {
    const haystack = `${c.name} ${c.jurisdictions.map(jurisdictionLabel).join(" ")}`.toLocaleLowerCase("en-US");
    if (query && !haystack.includes(query)) return false;
    if (filters.year && !c.ballots.some((b) => b.date.startsWith(filters.year!))) return false;
    if (filters.status && cycleStatus(c, today) !== filters.status) return false;
    if (filters.jurisdiction && !c.jurisdictions.includes(filters.jurisdiction)) return false;
    return true;
  });
}

/** The date a cycle is filed and sorted under: its live ballot's. */
export function cycleDate(cycle: PublicCycleSummary, today: string): string {
  return liveBallot(cycle.ballots, today)?.date ?? "";
}

/** Ledger groups by year, newest first; within a year, newest first. */
export function groupByYear(cycles: PublicCycleSummary[], today: string): Map<string, PublicCycleSummary[]> {
  const sorted = [...cycles].sort(
    (a, b) => cycleDate(b, today).localeCompare(cycleDate(a, today)) || a.name.localeCompare(b.name)
  );
  const groups = new Map<string, PublicCycleSummary[]>();
  for (const c of sorted) {
    const year = cycleDate(c, today).slice(0, 4) || "Undated";
    groups.set(year, [...(groups.get(year) ?? []), c]);
  }
  return groups;
}

/**
 * The featured cycle is the current one nearest today, else the soonest
 * upcoming; the strip beside it holds up to three more upcoming cycles.
 */
export function featuredAndUpcoming(
  cycles: PublicCycleSummary[],
  today: string
): { featured: PublicCycleSummary | null; upcoming: PublicCycleSummary[] } {
  const t = dayNumber(today);
  const distance = (c: PublicCycleSummary) => Math.min(...c.ballots.map((b) => Math.abs(dayNumber(b.date) - t)));
  const current = cycles.filter((c) => cycleStatus(c, today) === "current").sort((a, b) => distance(a) - distance(b));
  const upcoming = cycles
    .filter((c) => cycleStatus(c, today) === "upcoming")
    .sort((a, b) => (cycleDate(a, today) || "9999").localeCompare(cycleDate(b, today) || "9999"));
  const featured = current[0] ?? upcoming[0] ?? null;
  return { featured, upcoming: upcoming.filter((c) => c !== featured).slice(0, 3) };
}
